// src/pages/user/Support.js
// Page Support/À propos - traduite FR/EN, positionnement pro - NE PAS MODIFIER

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import { APP_CONFIG } from '../../constants/config';

const Support = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();

  const handleWhatsApp = () => {
    const phone = APP_CONFIG.SUPPORT_WHATSAPP.replace(/\D/g, '');
    window.open(`https://wa.me/${phone}`, '_blank');
  };

  const handleEmail = () => {
    window.location.href = `mailto:${APP_CONFIG.SUPPORT_EMAIL}`;
  };

  const advantages = [
    { icon: '🛡️', title: t('support.adv1Title'), text: t('support.adv1Text') },
    { icon: '📍', title: t('support.adv2Title'), text: t('support.adv2Text') },
    { icon: '💬', title: t('support.adv3Title'), text: t('support.adv3Text') },
    { icon: '🇨🇲', title: t('support.adv4Title'), text: t('support.adv4Text') },
  ];

  const faqItems = [
    { q: t('support.faq1q'), a: t('support.faq1a') },
    { q: t('support.faq2q'), a: t('support.faq2a') },
    { q: t('support.faq3q'), a: t('support.faq3a') },
    { q: t('support.faq4q'), a: t('support.faq4a') },
  ];

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.bg },
    header: {
      display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', position: 'sticky', top: 0,
      backgroundColor: theme.bg, zIndex: 50, borderBottom: `1px solid ${theme.borderSoft}`,
    },
    backButton: { background: 'none', border: 'none', color: theme.textPrimary, fontSize: '20px', cursor: 'pointer', padding: '4px' },
    title: { fontSize: '20px', fontWeight: '700', color: theme.textPrimary },
    heroSection: { padding: '24px 20px 8px', textAlign: 'center' },
    heroTitle: { fontSize: '20px', fontWeight: '800', color: theme.textPrimary, lineHeight: '1.3' },
    heroTitleAccent: { color: theme.gold },
    heroSubtitle: { fontSize: '14px', color: theme.textSecondary, marginTop: '8px', lineHeight: '1.5', maxWidth: '320px', marginLeft: 'auto', marginRight: 'auto' },
    advantagesGrid: { padding: '20px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
    advantageCard: {
      padding: '16px', backgroundColor: theme.bgCard, borderRadius: '14px',
      border: `1px solid ${theme.border}`, display: 'flex', flexDirection: 'column', gap: '6px',
    },
    advantageIcon: { fontSize: '26px' },
    advantageTitle: { fontSize: '13px', fontWeight: '700', color: theme.textPrimary, lineHeight: '1.3' },
    advantageText: { fontSize: '11.5px', color: theme.textSecondary, lineHeight: '1.4' },
    contactButtons: { display: 'flex', gap: '10px', padding: '8px 16px 20px' },
    contactButton: {
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
      padding: '16px', borderRadius: '14px', cursor: 'pointer', border: 'none', fontSize: '13px', fontWeight: '700',
    },
    whatsappBtn: { backgroundColor: 'rgba(37, 211, 102, 0.1)', border: '1px solid rgba(37, 211, 102, 0.3)', color: '#25D366' },
    emailBtn: { backgroundColor: 'rgba(66, 153, 225, 0.1)', border: '1px solid rgba(66, 153, 225, 0.3)', color: '#4299E1' },
    contactIcon: { fontSize: '24px' },
    sectionTitle: { fontSize: '16px', fontWeight: '700', color: theme.textPrimary, padding: '16px 16px 8px' },
    faqItem: { margin: '0 16px 12px', padding: '16px', backgroundColor: theme.bgCard, borderRadius: '14px', border: `1px solid ${theme.borderSoft}` },
    faqQuestion: { fontSize: '14px', fontWeight: '700', color: theme.gold, marginBottom: '8px' },
    faqAnswer: { fontSize: '13px', color: theme.textSecondary, lineHeight: '1.55' },
    footerBrand: { textAlign: 'center', padding: '28px 16px 24px', fontSize: '11px', color: theme.textMuted },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Fynho | Trouvez votre nouveau chez-vous au Cameroun</title>
        <meta name="description" content="Fynho vous accompagne pour trouver séjours, locations, terrains et services au Cameroun. Annonces vérifiées, contact direct et rapide." />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <div style={styles.title}>Fynho</div>
      </div>

      <div style={styles.heroSection}>
        <div style={styles.heroTitle}>
          {t('support.heroTitle1')} <span style={styles.heroTitleAccent}>{t('support.heroTitle2')}</span>
        </div>
        <div style={styles.heroSubtitle}>{t('support.heroSubtitle')}</div>
      </div>

      <div style={styles.advantagesGrid}>
        {advantages.map((item, index) => (
          <div key={index} style={styles.advantageCard}>
            <span style={styles.advantageIcon}>{item.icon}</span>
            <span style={styles.advantageTitle}>{item.title}</span>
            <span style={styles.advantageText}>{item.text}</span>
          </div>
        ))}
      </div>

      <div style={styles.sectionTitle}>{t('support.needHelp')}</div>
      <div style={styles.contactButtons}>
        <button style={{ ...styles.contactButton, ...styles.whatsappBtn }} onClick={handleWhatsApp}>
          <span style={styles.contactIcon}>💬</span>
          WhatsApp
        </button>
        <button style={{ ...styles.contactButton, ...styles.emailBtn }} onClick={handleEmail}>
          <span style={styles.contactIcon}>✉️</span>
          Email
        </button>
      </div>

      <div style={styles.sectionTitle}>{t('support.faqTitle')}</div>
      {faqItems.map((item, index) => (
        <div key={index} style={styles.faqItem}>
          <div style={styles.faqQuestion}>{item.q}</div>
          <div style={styles.faqAnswer}>{item.a}</div>
        </div>
      ))}

      <div style={styles.footerBrand}>Fynho © {new Date().getFullYear()} — Find Your New Home</div>
    </div>
  );
};

export default Support;